import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, api, getQueryFn } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { isValidReturnTo } from "@/lib/auth-utils";

interface UserApiResponse {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isSuperAdmin: boolean;
  totpEnabled: boolean;
  requireEnrollment: boolean;
  needsOnboarding: boolean;
  avatarUrl: string | null;
  locationCodes: string[];
  defaultLocationCode: string | null;
  roleSlugs?: string[];
}

interface User {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  totpEnabled: boolean;
  requireEnrollment: boolean;
  needsOnboarding: boolean;
  avatarUrl: string | null;
  locationCodes: string[];
  defaultLocationCode: string | null;
  roleSlugs: string[];
}

export function getDisplayName(user: User | null): string {
  if (!user) return "";
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return fullName || user.username;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: { email: string; password: string; username: string; token: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export type { User };

const AuthContext = createContext<AuthContextType | null>(null);

function transformUserResponse(data: UserApiResponse | null): User | null {
  if (!data) return null;
  return {
    ...data,
    isAdmin: data.role === "admin",
    isSuperAdmin: data.isSuperAdmin,
    totpEnabled: data.totpEnabled ?? false,
    roleSlugs: data.roleSlugs ?? [],
  };
}

function getPostLoginDestination(): string {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get("returnTo");
  return isValidReturnTo(returnTo) ? returnTo! : "/dashboard";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();

  const { data: rawUser, isLoading, refetch } = useQuery<UserApiResponse | null>({
    queryKey: ["/api/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const user = transformUserResponse(rawUser ?? null);

  const loginMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api.post("/api/auth/login", data),
    onSuccess: async (userPayload: UserApiResponse) => {
      queryClient.setQueryData(["/api/auth/me"], userPayload);
      setLocation(getPostLoginDestination());
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post("/api/auth/logout"),
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      setLocation("/login");
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data: { email: string; password: string; username: string; token: string }) =>
      api.post("/api/auth/register", data),
    onSuccess: async (userPayload: UserApiResponse) => {
      queryClient.setQueryData(["/api/auth/me"], userPayload);
      setLocation(getPostLoginDestination());
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isLoading,
        login: async (email, password) => {
          await loginMutation.mutateAsync({ email, password });
        },
        logout: async () => {
          await logoutMutation.mutateAsync();
        },
        register: async (data) => {
          await registerMutation.mutateAsync(data);
        },
        refreshUser: async () => {
          await refetch();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
